/**
 * Typed client for the per-SITE settlement-rail override (ADR-0096):
 *
 *   GET /v1/sites/:site_id/settlement-rail  — owner/admin reads the site's
 *       explicit override (`null` = inherit), the effective rail the selector
 *       would use, the account default, and which rails are connected.
 *   PUT /v1/sites/:site_id/settlement-rail  — owner/admin sets the override.
 *       Body `{ rail: 'stripe' | 'shopify' | null }`; `null` clears the
 *       override (inherit the account default). A non-null rail with no
 *       connected integration is rejected `rail_not_connected` (409).
 *
 * Mirrors `account-settlement-rail.ts`; the account control is the default,
 * the site control is the override that wins for THIS site.
 */

import { ApiError, type HttpClient } from './http-client.js';
import type { ConnectedRails, SettlementRail } from './account-settlement-rail.js';

/** `null` override = inherit the account default. */
export type SiteRailOverride = SettlementRail | null;

export type GetSiteSettlementRailResult =
  | {
      readonly kind: 'ok';
      /** The site's explicit override; `null` = inherit. */
      readonly override: SiteRailOverride;
      /** The rail the selector actually uses (override ?? account default). */
      readonly effective: SettlementRail;
      readonly account_default: SettlementRail;
      readonly connected: ConnectedRails;
    }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type PutSiteSettlementRailResult =
  | { readonly kind: 'ok'; readonly override: SiteRailOverride }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'rail_not_connected'; readonly rail: SettlementRail }
  | { readonly kind: 'bad_request' }
  | { readonly kind: 'error'; readonly message: string };

interface GetWire {
  readonly rail: string | null;
  readonly effective: string;
  readonly account_default: string;
  readonly connected?: { stripe?: unknown; shopify?: unknown };
}

interface PutWire {
  readonly rail: string | null;
}

export async function getSiteSettlementRail(
  http: HttpClient,
  site_id: string,
): Promise<GetSiteSettlementRailResult> {
  try {
    const res = await http.request<GetWire>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/settlement-rail`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const effective = parseRail(res.effective);
    const account_default = parseRail(res.account_default);
    if (effective === null || account_default === null) {
      return { kind: 'error', message: 'Server returned an unknown rail value.' };
    }
    // `rail` is the override: a valid rail, or null (inherit). An unknown
    // non-null string is a server contract drift.
    const override = res.rail === null ? null : parseRail(res.rail);
    if (res.rail !== null && override === null) {
      return { kind: 'error', message: 'Server returned an unknown override value.' };
    }
    return {
      kind: 'ok',
      override,
      effective,
      account_default,
      connected: {
        stripe: res.connected?.stripe === true,
        shopify: res.connected?.shopify === true,
      },
    };
  } catch (err) {
    return mapGetError(err);
  }
}

export async function putSiteSettlementRail(
  http: HttpClient,
  site_id: string,
  rail: SiteRailOverride,
): Promise<PutSiteSettlementRailResult> {
  try {
    const res = await http.request<PutWire>({
      method: 'PUT',
      path: `/v1/sites/${encodeURIComponent(site_id)}/settlement-rail`,
      body: { rail },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const echoed = res.rail === null ? null : parseRail(res.rail);
    if (res.rail !== null && echoed === null) {
      return { kind: 'error', message: 'Server returned an unknown rail value.' };
    }
    return { kind: 'ok', override: echoed };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'conflict' && err.server_reason === 'rail_not_connected') {
        // Only reached for a non-null rail (clearing never hits the gate).
        return { kind: 'rail_not_connected', rail: (rail ?? 'stripe') as SettlementRail };
      }
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'bad_request') return { kind: 'bad_request' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

function mapGetError(err: unknown): GetSiteSettlementRailResult {
  if (err instanceof ApiError) {
    if (err.kind === 'forbidden') return { kind: 'forbidden' };
    if (err.kind === 'not_found') return { kind: 'not_found' };
    return { kind: 'error', message: explainApiError(err) };
  }
  return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
}

function parseRail(raw: string | undefined): SettlementRail | null {
  return raw === 'stripe' || raw === 'shopify' ? raw : null;
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Settlement-rail service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
